import React, { useState } from 'react'
import { Alert, Button, Divider, Flex, Popover, Skeleton, Space, Table, Typography, Tag } from 'antd'
import { DiffOutlined, HistoryOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { isNumber } from 'lodash'
import PatternIcon from '../../../../../components/common/PatternIcon'
import logo from '../../../../../../assets/images/sense_logo2.png'
import logoWhite from '../../../../../../assets/images/login-header/sense-logo-white.png'
import MarkdownRenderer from '../../../../../components/common/markdown-renderer'
import TextDiffViewer from '../../../components/section-menu/rubric-refinement/chat/messages/DiffViewer'
import AiIcon from '../../../../../components/common/icons/AiIcon'
import useRubricContext from '../../../../../hooks/useRubricContext'
const { Title, Text, Paragraph } = Typography

const TextDiff = ({ oldText = '', newText = '', popoverTitle }) => {
    return (
        <Space direction='vertical'>
            <Flex justify='space-between' align='center'>
                <Text type='secondary'>
                    <AiIcon /> Enhanced Description
                </Text>
                {oldText && (
                    <Popover
                        content={
                            <div style={{ maxWidth: '600px', maxHeight: '400px', overflowY: 'auto' }}>
                                <MarkdownRenderer content={oldText} />
                            </div>
                        }
                        title={popoverTitle}
                        trigger='click'
                        placement='top'
                    >
                        <Button type='text' size='small' icon={<HistoryOutlined />}>
                            View old
                        </Button>
                    </Popover>
                )}
            </Flex>
            <MarkdownRenderer content={newText} />
        </Space>
    )
}

const RubricContent = ({ questionRubric, isDiffView }) => {
    const rubric = questionRubric?.rubric || {}
    const categories = questionRubric?.categories || []

    return (
        <div>
            <Title level={3} style={{ margin: 0 }}>
                {rubric.name}
            </Title>
            {rubric.content && (
                <Paragraph>
                    <MarkdownRenderer content={rubric.content} />
                </Paragraph>
            )}
            {rubric.gradingReferenceContent && (
                <Paragraph>
                    <MarkdownRenderer content={rubric.gradingReferenceContent} />
                </Paragraph>
            )}
            {isNumber(rubric.weight) && (
                <Paragraph>
                    <Text strong>Weight:</Text> {rubric.weight} points
                </Paragraph>
            )}
            <Title level={4} style={{ margin: 0 }}>
                Categories
            </Title>
            <Table
                dataSource={categories}
                rowKey='id'
                pagination={false}
                size='small'
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                        minWidth: 180,
                        render: (name, category) => (
                            <Space>
                                <PatternIcon id={category.id} colorId={category.colorId} showId={false} />
                                <Text strong>{name}</Text>
                            </Space>
                        ),
                    },
                    {
                        title: 'Points',
                        dataIndex: 'grade',
                        key: 'grade',
                        width: 60,
                        render: grade => (grade !== null ? grade : '-'),
                    },
                    {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        minWidth: 400,
                        render: (description, category) => {
                            if (!description) return '-'
                            if (category.comparisonDescription != null && category.comparisonDescription !== '') {
                                const current = String(category.description || '')
                                const comparison = String(category.comparisonDescription || '')
                                const isFinalized =
                                    category.descriptionChangeStatus === 'Done' ||
                                    category.descriptionChangeStatus === 'finalized'
                                const leftValue = isFinalized ? comparison : current
                                const rightValue = isFinalized ? current : comparison
                                const isDescChanged = current !== comparison

                                return (
                                    <Space direction='vertical' size={8} style={{ width: '100%' }}>
                                        <Flex justify='space-between' align='center'>
                                            {category.descriptionChangeStatus && (
                                                <Tag
                                                    color={
                                                        category.descriptionChangeStatus === 'pending'
                                                            ? 'orange'
                                                            : 'green'
                                                    }
                                                >
                                                    {category.descriptionChangeStatus === 'pending'
                                                        ? 'Pending'
                                                        : 'Finalized'}
                                                </Tag>
                                            )}
                                        </Flex>
                                        {isDiffView ? (
                                            <TextDiffViewer
                                                oldValue={leftValue}
                                                newValue={rightValue}
                                                showDiff={true}
                                            />
                                        ) : (
                                            <>
                                                {isDescChanged ? (
                                                    <TextDiff
                                                        oldText={leftValue}
                                                        newText={rightValue}
                                                        popoverTitle='Old Description'
                                                    />
                                                ) : (
                                                    <MarkdownRenderer content={rightValue} />
                                                )}
                                            </>
                                        )}
                                    </Space>
                                )
                            }
                            return <MarkdownRenderer content={description} />
                        },
                    },
                    {
                        title: 'Pre-defined feedback',
                        dataIndex: 'feedback',
                        key: 'feedback',
                        render: feedback => feedback || '-',
                    },
                ]}
            />
        </div>
    )
}

const RubricPreview = () => {
    const assignmentName = useSelector(assignmentSelectors.name)
    const [isDiffView, setIsDiffView] = useState(true)
    const themeMode = useSelector(state => state.theme?.mode || 'light')
    const logoSrc = themeMode === 'dark' ? logoWhite : logo

    const { questions, isFetching } = useRubricContext()

    if (isFetching) return <Skeleton active />
    if (!questions) return <Alert message="Failed to load questions" type='error' showIcon />

    const checkQuestionRubricForUpdates = questionRubric => {
        if (!questionRubric) return false
        const categories = questionRubric.categories || []
        const hasCategoryUpdates = categories.some(cat => cat?.comparisonDescription != null && cat.comparisonDescription !== '')
        return hasCategoryUpdates
    }

    const checkQuestionForUpdates = question => {
        if (!question) return false
        const hasGradingReferenceUpdates =
            question.comparisonGradingReference != null && question.comparisonGradingReference !== ''
        const hasRubricUpdates =
            Array.isArray(question.questionRubrics) &&
            question.questionRubrics.some(qr => checkQuestionRubricForUpdates(qr))
        return hasGradingReferenceUpdates || hasRubricUpdates
    }

    const hasAnyUpdates =
        Array.isArray(questions) &&
        questions.length > 0 &&
        questions.some(question => checkQuestionForUpdates(question))

    return (
        <div>
            <Flex justify='space-between' align='center' style={{ marginBottom: '16px' }}>
                <Title level={1} style={{ fontSize: '1.8rem', margin: 0 }}>
                    {assignmentName}
                </Title>
                <Flex align='center' gap={16}>
                    {hasAnyUpdates && (
                        <Button type='text' icon={<DiffOutlined />} onClick={() => setIsDiffView(!isDiffView)}>
                            {isDiffView ? 'View Updated Only' : 'View Difference'}
                        </Button>
                    )}
                    <a href='https://www.sense.education/'>
                        <img
                            src={logoSrc}
                            alt='Sense Education'
                            style={{
                                width: '120px',
                                objectFit: 'contain',
                            }}
                        />
                    </a>
                </Flex>
            </Flex>
            {questions.map(question => (
                <div key={question.id}>
                    <Title level={2} style={{ margin: 0, fontSize: '1.5rem' }}>
                        {question.name}
                    </Title>
                    <Paragraph>
                        <Text>{question.content}</Text>
                    </Paragraph>
                    {(question.questionRubrics || []).map(questionRubric => (
                        <div key={questionRubric.id} style={{ marginLeft: '1.5rem', marginTop: '1rem' }}>
                            <RubricContent
                                questionRubric={questionRubric}
                                isDiffView={isDiffView}
                            />
                        </div>
                    ))}
                    <Divider />
                </div>
            ))}
        </div>
    )
}

export default RubricPreview
